import classNames from "classnames";
import type React from "react";
import "./Icon.css";

/* Symbol name from: https://fonts.google.com/icons */
type MaterialDesignIconName =
    | "add"
    | "add_link"
    | "add_photo_alternate"
    | "arrow_back"
    | "arrow_forward"
    | "arrow_downward"
    | "arrow_upward"
    | "assignment_add"
    | "attach_file"
    | "attach_file_add"
    | "autorenew"
    | "calendar_month"
    | "calendar_today"
    | "call"
    | "chevron_left"
    | "chevron_right"
    | "check"
    | "close"
    | "cancel"
    | "content_copy"
    | "content_paste"
    | "commute"
    | "crop"
    | "description"
    | "delete"
    | "drag_indicator"
    | "edit"
    | "error"
    | "event_available"
    | "expand_less"
    | "expand_more"
    | "favorite"
    | "flight"
    | "filter_list"
    | "forum"
    | "import_contacts"
    | "label"
    | "link"
    | "list"
    | "logout"
    | "lock"
    | "local_activity"
    | "local_bar"
    | "location_on"
    | "handshake"
    | "hotel"
    | "keyboard_tab"
    | "keyboard_arrow_down"
    | "notifications"
    | "notification_add"
    | "mark_unread_chat_alt"
    | "mic"
    | "more_vert"
    | "more_horiz"
    | "open_in_new"
    | "picture_as_pdf"
    | "person_add"
    | "photo_frame"
    | "photo_camera"
    | "priority_high"
    | "problem"
    | "refresh"
    | "restore_from_trash"
    | "restaurant_menu"
    | "reply"
    | "rotate_right"
    | "room_service"
    | "route"
    | "schedule"
    | "send"
    | "search"
    | "share"
    | "shopping_bag"
    | "sensors"
    | "settings"
    | "spa"
    | "star_rate"
    | "thumb_up"
    | "thumb_down"
    | "train"
    | "travel_explore"
    | "undo"
    | "unfold_more"
    | "unfold_less"
    | "unpublished"
    | "vpn_key"
    | "question_mark"
    | "collapse_content"
    | "expand_content"
    | "airplane_ticket"
    | "emoji_food_beverage"
    | "airlines"
    | "emoji_transportation"
    | "parking_valet"
    | "stars_2"
    | "concierge"
    | "redeem"
    | "villa"
    | "local_activity"
    | "shopping_bag";

type BaseIcon = (className?: string) => (props: Props & IconProps) => React.ReactNode;

export type Props = {
    className?: string;
};

export type IconProps = {
    style?: React.CSSProperties;
};

function BaseMaterialIcon(name: MaterialDesignIconName) {
    return function BaseIcon(baseClassName?: string) {
        return function Icon(props: Props & IconProps) {
            const { className, style } = props;

            return (
                <span
                    className={classNames(
                        "web-lib__icon",
                        "material-symbols-outlined",
                        baseClassName,
                        className,
                    )}
                    style={style}
                >
                    {name}
                </span>
            );
        };
    };
}

function Add(baseIcon: BaseIcon, className: string) {
    return (newClassName?: string) => baseIcon(classNames(className, newClassName));
}

const Fill = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Fill: baseComponent("web-lib__icon--fill"),
    });

const MdColors = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Text: Fill(Add(baseComponent, "text-text-primary")),
        TextInverse: Fill(Add(baseComponent, "text-text-inverse-primary")),
        Inactive: Fill(Add(baseComponent, "text-status-inactive")),
        Error: Fill(Add(baseComponent, "text-status-error")),
        Warning: Fill(Add(baseComponent, "text-status-warning")),
        Success: Fill(Add(baseComponent, "text-status-success")),
        NavigationSelected: Fill(Add(baseComponent, "text-text-navigation-selected")),
        NavigationUnselected: Fill(
            Add(baseComponent, "text-text-navigation-unselected"),
        ),
    });

const MdSizes = (baseComponent: BaseIcon) =>
    Object.assign(baseComponent(), {
        Large: MdColors(Add(baseComponent, "web-lib__icon--large")),
        Medium: MdColors(Add(baseComponent, "web-lib__icon--medium")),
        Small: MdColors(Add(baseComponent, "web-lib__icon--small")),
        XSmall: MdColors(Add(baseComponent, "web-lib__icon--xsmall")),
    });

const BaseMaterialIconDefault = (name: MaterialDesignIconName) =>
    Fill(Add(BaseMaterialIcon(name), "text-text-tertiary"));

const MaterialIcon = (name: MaterialDesignIconName) =>
    Object.assign(BaseMaterialIconDefault(name), MdSizes(BaseMaterialIcon(name)));

/* TODO: together with so many colors this component grows to big that typescript limit exceeds */
const Icon = {
    Add: MaterialIcon("add"),
    AddPhotoAlternate: MaterialIcon("add_photo_alternate"),
    /* AddLink: MaterialIcon("add_link"), */
    ArrowBack: MaterialIcon("arrow_back"),
    ArrowForward: MaterialIcon("arrow_forward"),
    ArrowDownward: MaterialIcon("arrow_downward"),
    /* ArrowUpward: MaterialIcon("arrow_upward"),
    AssignmentAdd: MaterialIcon("assignment_add"), */
    AttachFile: MaterialIcon("attach_file"),
    AttachFileAdd: MaterialIcon("attach_file_add"),
    AutoRenew: MaterialIcon("autorenew"),
    CalendarMonth: MaterialIcon("calendar_month"),
    CalendarToday: MaterialIcon("calendar_today"),
    Call: MaterialIcon("call"),
    ChevronLeft: MaterialIcon("chevron_left"),
    ChevronRight: MaterialIcon("chevron_right"),
    Check: MaterialIcon("check"),
    Close: MaterialIcon("close"),
    Cancel: MaterialIcon("cancel"),
    /* ContentCopy: MaterialIcon("content_copy"),
    ContentPaste: MaterialIcon("content_paste"),
    Commute: MaterialIcon("commute"),
    Crop: MaterialIcon("crop"), */
    Description: MaterialIcon("description"),
    /* Delete: MaterialIcon("delete"),
    DragIndicator: MaterialIcon("drag_indicator"),
    ExpandLess: MaterialIcon("expand_less"), */
    EventAvailable: MaterialIcon("event_available"),
    /* ExpandMore: MaterialIcon("expand_more"),
    Edit: MaterialIcon("edit"), */
    Error: MaterialIcon("error"),
    /* FilterList: MaterialIcon("filter_list"),
    Flight: MaterialIcon("flight"), */
    Forum: MaterialIcon("forum"),
    /* Favorite: MaterialIcon("favorite"),
    ImportContacts: MaterialIcon("import_contacts"),
    Label: MaterialIcon("label"),
    Link: MaterialIcon("link"),
    List: MaterialIcon("list"),
    Logout: MaterialIcon("logout"), */
    Lock: MaterialIcon("lock"),
    /* LocalActivity: MaterialIcon("local_activity"),
    LocalBar: MaterialIcon("local_bar"), */
    LocationOn: MaterialIcon("location_on"),
    KeyBoardArrowDown: MaterialIcon("keyboard_arrow_down"),
    /* Handshake: MaterialIcon("handshake"),
    Hotel: MaterialIcon("hotel"),
    KeyBoardTab: MaterialIcon("keyboard_tab"),
    Notifications: MaterialIcon("notifications"),
    NotificationAdd: MaterialIcon("notification_add"),
    MarkUnreadChatAlt: MaterialIcon("mark_unread_chat_alt"), */
    Mic: MaterialIcon("mic"),
    /* MoreVert: MaterialIcon("more_vert"),
    MoreHoriz: MaterialIcon("more_horiz"),
    OpenInNew: MaterialIcon("open_in_new"),
    PhotoFrame: MaterialIcon("photo_frame"),
    PhotoCamera: MaterialIcon("photo_camera"),
    PictureAsPdf: MaterialIcon("picture_as_pdf"),
    PersonAdd: MaterialIcon("person_add"),
    PriorityHigh: MaterialIcon("priority_high"),
    Problem: MaterialIcon("problem"),
    Refresh: MaterialIcon("refresh"),
    RestoreFromTrash: MaterialIcon("restore_from_trash"),
    RestaurantMenu: MaterialIcon("restaurant_menu"), */
    Reply: MaterialIcon("reply"),
    /* RotateRight: MaterialIcon("rotate_right"),
    Route: MaterialIcon("route"), */
    RoomService: MaterialIcon("room_service"),
    /* Schedule: MaterialIcon("schedule"), */
    Send: MaterialIcon("send"),
    Search: MaterialIcon("search"),
    Share: MaterialIcon("share"),
    ThumbUp: MaterialIcon("thumb_up"),
    StarRate: MaterialIcon("star_rate"),
    /* ShoppingBag: MaterialIcon("shopping_bag"),
    Sensors: MaterialIcon("sensors"),
    Settings: MaterialIcon("settings"),
    Spa: MaterialIcon("spa"),
    ThumbDown: MaterialIcon("thumb_down"),
    Train: MaterialIcon("train"), */
    TravelExplore: MaterialIcon("travel_explore"),
    /* Undo: MaterialIcon("undo"),
    UnfoldMore: MaterialIcon("unfold_more"),
    UnfoldLess: MaterialIcon("unfold_less"),
    Unpublished: MaterialIcon("unpublished"),
    VpnKey: MaterialIcon("vpn_key"),
    QuestionMark: MaterialIcon("question_mark"), */
    CollapseContent: MaterialIcon("collapse_content"),
    ExpandContent: MaterialIcon("expand_content"),
    AirplaneTicket: MaterialIcon("airplane_ticket"),
    EmojiFoodBeverage: MaterialIcon("emoji_food_beverage"),
    Airlines: MaterialIcon("airlines"),
    EmojiTransportation: MaterialIcon("emoji_transportation"),
    ParkingValet: MaterialIcon("parking_valet"),
    Stars2: MaterialIcon("stars_2"),
    Concierge: MaterialIcon("concierge"),
    Redeem: MaterialIcon("redeem"),
    Villa: MaterialIcon("villa"),
    LocalActivity: MaterialIcon("local_activity"),
    ShoppingBag: MaterialIcon("shopping_bag"),
};

export default Icon;

export type MdIconName = keyof typeof Icon;
